import ProductData from 'lib/product-data';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';

export type CartSkuQuantity = Record<string, number>;

// Instagram Product IDs/Content IDs here:
// https://business.facebook.com/commerce/catalogs/1534406074180467/products/?business_id=383757972113062
// Ask for access if you need it

// We don't need this at the moment because all the product ids/content ids are SKUs
// OR, they are syn-wkX which we can fuzzy map below
// const instagramProductIdToSkuMap: Record<string, string> = {
//   '12345': 'syn-wk',
//   '23456': 'dm-wk',
//   '34567': 'am-wk',
//   '45678': 'pm-wk',
//   '56789': 'pds-wk',
//   '67890': 'vs-wk',
// };

// Fuzzy SKU mapping for syn-wkX (X is a number) => syn-wk, but syn-wk-3mo and syn-wk-6mo map to themselves
function fuzzySkuMap(sku: string): string | undefined {
  if (/^syn-wk\d+$/.test(sku)) {
    return PRODUCT_SKUS.DAILY_SYNBIOTIC;
  }
  if (sku === 'syn-wk-3mo' || sku === 'syn-wk-6mo') {
    return sku;
  }
  return sku;
}

export const VS_SKUS: string[] = [
  PRODUCT_SKUS.VAGINAL_SYNBIOTIC,
  PRODUCT_SKUS.VAGINAL_SYNBIOTIC_SUSTAIN,
];

// Parse RFC3986-encoded product list, e.g. products=12345%3A3%2C23456%3A1
export const parseProductQuantities = (
  productsParam: string | null,
  isVsOutOfStock?: boolean,
): Record<string, number> => {
  if (!productsParam) return {};
  try {
    const decoded = decodeURIComponent(productsParam);
    const result = decoded
      .split(',')
      .map((entry) => entry.split(':'))
      .filter(([id, qty]) => id && qty)
      .reduce(
        (acc, [id, qty]) => {
          const sku = id;
          const mappedSku = fuzzySkuMap(sku);
          if (
            typeof mappedSku === 'string' &&
            Object.prototype.hasOwnProperty.call(ProductData, mappedSku)
          ) {
            acc[mappedSku] = Number(qty);
          }
          return acc;
        },
        {} as Record<string, number>,
      );

    if (isVsOutOfStock) {
      VS_SKUS.forEach((sku) => delete result[sku]);
    }

    return result;
  } catch {
    return {};
  }
};

export const updateCartOnLoad = (
  cartFromLocalStorage: CartSkuQuantity,
  urlCartItems: string | null,
  urlProducts: string | null,
  isVsOutOfStock?: boolean,
): { updatedCart: CartSkuQuantity; addedItems: CartSkuQuantity } => {
  const updatedCart: CartSkuQuantity = { ...cartFromLocalStorage };
  const addedItems: CartSkuQuantity = {};

  if (urlProducts) {
    const parsedProducts = parseProductQuantities(urlProducts, isVsOutOfStock);
    return {
      updatedCart: {
        ...parsedProducts,
      },
      addedItems: {
        ...parsedProducts,
      },
    };
  }

  if (urlCartItems) {
    const urlProductCodes = urlCartItems
      .split(',')
      .map((code) => code.trim().toLowerCase())
      .filter(Boolean);

    urlProductCodes.forEach((sku) => {
      const mappedSku = fuzzySkuMap(sku);

      // Validate mappedSku exists in ProductData
      if (
        typeof mappedSku !== 'string' ||
        !Object.prototype.hasOwnProperty.call(ProductData, mappedSku)
      ) {
        return;
      }

      // Do not increment if item already exists in cartFromLocalStorage
      if (cartFromLocalStorage[mappedSku]) {
        return;
      }

      if (updatedCart[mappedSku]) {
        updatedCart[mappedSku] += 1;
      } else {
        updatedCart[mappedSku] = 1;
      }

      if (addedItems[mappedSku]) {
        addedItems[mappedSku] += 1;
      } else {
        addedItems[mappedSku] = 1;
      }
    });
  }

  return { updatedCart, addedItems };
};
